import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { TuiPanel } from "@/components/tui/TuiPanel";
import { TuiPrompt } from "@/components/tui/TuiPrompt";
import { TuiButton } from "@/components/tui/TuiButton";
import { tracks } from "@/data/courses";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isTrackUnlocked } from "@/lib/progress";
import { useUserProgress } from "@/hooks/useUserProgress";

const FRISYRE_ASCII = String.raw`
███████╗██████╗ ██╗███████╗██╗   ██╗██████╗ ███████╗
██╔════╝██╔══██╗██║██╔════╝╚██╗ ██╔╝██╔══██╗██╔════╝
█████╗  ██████╔╝██║███████╗ ╚████╔╝ ██████╔╝█████╗
██╔══╝  ██╔══██╗██║╚════██║  ╚██╔╝  ██╔══██╗██╔══╝
██║     ██║  ██║██║███████║   ██║   ██║  ██║███████╗
╚═╝     ╚═╝  ╚═╝╚═╝╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝`;

const motd = `> WARNING: UNAUTHORIZED ACCESS PROHIBITED.
> You are entering a free, structured cybersecurity training grid.
> Methodologies taught here are for sanctioned blue & red team work only.

This OS ships with a complete curriculum: Linux & networking primitives,
SOC analysis, web exploitation, AD attacks, mobile reversing, OSCP prep,
and a live Kali playground. Zero subscriptions. Zero gatekeeping.`;

const liveLog = [
  { t: "14:22:01", c: "muted", m: "INBOUND  192.168.1.47 → :22" },
  { t: "14:22:03", c: "muted", m: "AUTH_FAIL user='admin' (rsa)" },
  { t: "14:22:04", c: "muted", m: "AUTH_FAIL user='root'  (pwd)" },
  { t: "14:22:04", c: "muted", m: "CONN_DROP timeout" },
  { t: "14:22:18", c: "muted", m: "INBOUND  10.0.5.99  → :443" },
  { t: "14:22:19", c: "destructive", m: "SEGFAULT pid=4921 (nginx)" },
  { t: "14:22:20", c: "primary", m: "SHELL_SPAWNED tty2 (uid=0)" },
  { t: "14:22:20", c: "muted", m: "EXEC curl -s … | bash" },
  { t: "14:22:31", c: "warning", m: "RATE_LIMIT 11 req/s on /api" },
  { t: "14:22:42", c: "primary", m: "LAB_PASSED foundations/01_linux" },
];

function useNow() {
  const [d, setD] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setD(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return d;
}

const Index = () => {
  const now = useNow();
  const { user } = useAuth();
  const { data: progress } = useUserProgress();
  const trackIds = tracks.map((t) => t.id);
  const totalHours = tracks.reduce((sum, t) => sum + t.estimatedHours, 0);
  const totalLessons = tracks.reduce(
    (sum, t) => sum + t.modules.reduce((mSum, m) => mSum + m.lessons.length, 0),
    0,
  );
  const totalQuestions = tracks.reduce((sum, t) => sum + t.examQuestionCount, 0);

  const homeJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "FRISYRE Cybersecurity Tracks",
      description: "Free cybersecurity learning tracks from Linux fundamentals to OSCP-level offensive security, blue team, cloud, mobile, malware analysis and GRC.",
      numberOfItems: tracks.length,
      itemListElement: tracks.map((t, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://frisyre.com/track/${t.id}`,
        name: t.name,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Is FRISYRE really free?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes — every track, lesson, lab, exam and certificate on FRISYRE is 100% free. No credit card, no subscription, no upsell. FRISYRE curates the best public learning resources into a single OSCP-level curriculum.",
          },
        },
        {
          "@type": "Question",
          name: "What can I learn on FRISYRE?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Linux and networking fundamentals, web hacking, Active Directory attacks, Kali Linux, Metasploit, privilege escalation, OSCP-style enumeration and exploitation, blue-team detection and SOC operations, cloud security (AWS, Azure, GCP, Kubernetes), mobile and iOS/Android testing, malware analysis, digital forensics, incident response, GRC, bug bounty methodology, AI/LLM security, Web3 smart-contract auditing, purple-team adversary emulation, threat hunting, network security engineering and cybersecurity career skills — across 19 structured tracks.",
          },
        },
        {
          "@type": "Question",
          name: "Do I get a certificate?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Pass the timed exam at the end of each track (80% to pass) to unlock a signed, printable, verifiable FRISYRE certificate of completion. Finish all tracks to unlock the FRISYRE Master certificate.",
          },
        },
        {
          "@type": "Question",
          name: "Is FRISYRE good for OSCP preparation?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. FRISYRE includes a dedicated OSCP-prep track that mirrors the OffSec PEN-200 syllabus, plus an in-browser Kali playground with 60+ tools so you can practice real commands without installing anything.",
          },
        },
        {
          "@type": "Question",
          name: "How does FRISYRE compare to TryHackMe or HackTheBox?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "FRISYRE is a structured curriculum with sequenced tracks, timed exams and signed certificates — fully free. It complements lab platforms like TryHackMe and HackTheBox by giving you the underlying theory, tradecraft and assessments without any paywall.",
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground tui-scanlines">
      <Seo
        title="Learn Ethical Hacking Free"
        description="FRISYRE — free cybersecurity academy. 19 structured tracks from Linux to OSCP-level offense, in-browser Kali labs, timed exams, signed certificates."
        path="/"
        keywords="learn ethical hacking online free, free cybersecurity course, free OSCP prep, kali linux course, free penetration testing course, ethical hacking certification, free cybersecurity certification, bug bounty training, red team training, blue team training, SOC analyst training, malware analysis course, active directory hacking, cloud security free, in-browser kali, hack the box alternative, tryhackme alternative"
        jsonLd={homeJsonLd}
      />
      <Navbar />

      <main className="px-2 md:px-5 py-4 md:py-6 flex flex-col gap-5 md:gap-6 max-w-[1600px] mx-auto">
        {/* ───── HERO: motd ───── */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          <div className="lg:col-span-8">
            <TuiPanel
              label="tty1 : /bin/bash"
              right={`${now.toISOString().replace("T", " ").slice(0, 19)} UTC`}
              innerClassName="p-5 md:p-6 flex flex-col gap-4 min-h-[520px]"
            >
              <TuiPrompt>cat /etc/motd</TuiPrompt>

              <h1 className="sr-only">
                FRISYRE — Learn Ethical Hacking Online Free. Get Certified. Pay Nothing.
              </h1>
              <pre
                aria-hidden="true"
                className="text-primary text-[8px] sm:text-[10px] md:text-xs leading-[1.05] whitespace-pre overflow-x-auto my-2"
              >
                {FRISYRE_ASCII}
              </pre>

              <pre className="whitespace-pre-wrap text-[13px] leading-relaxed text-foreground/85 max-w-[72ch]">
                {motd}
              </pre>

              <div className="mt-auto pt-3 border-t border-border/60 flex flex-col gap-3">
                <TuiPrompt caret>
                  <Link to="/dashboard" className="hover:underline underline-offset-4">
                    ./deploy_session
                  </Link>{" "}
                  <span className="text-muted-foreground">
                    --user=$ME --target=foundations
                  </span>
                </TuiPrompt>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Link to="/dashboard">
                    <TuiButton variant="primary">[ exec ./start ]</TuiButton>
                  </Link>
                  <a href="#tracks">
                    <TuiButton variant="ghost">[ ls ./tracks ]</TuiButton>
                  </a>
                  <Link to="/playground">
                    <TuiButton variant="ghost">[ ssh kali@lab ]</TuiButton>
                  </Link>
                  <Link to="/compare">
                    <TuiButton variant="ghost">[ vs htb/thm/oscp ]</TuiButton>
                  </Link>
                </div>
              </div>
            </TuiPanel>
          </div>

          {/* Side rail: sys monitor + live log */}
          <aside className="lg:col-span-4 flex flex-col gap-5 md:gap-6">
            <TuiPanel label="sys_monitor" innerClassName="p-4 md:p-5">
              <div className="flex flex-col gap-2.5 text-xs">
                <Stat k="HOURS_INDEXED" v={`${totalHours.toLocaleString()}`} />
                <Stat k="LESSONS" v={totalLessons.toString()} />
                <Stat k="QUESTIONS" v={totalQuestions.toString()} />
                <Stat k="TRACKS" v={tracks.length.toString()} />
                <Stat k="CERTS_ISSUABLE" v={tracks.length.toString()} accent="primary" />
                <Stat k="THREAT_DEFCON" v="LEVEL 4" accent="warning" />
                <Stat k="COST_USD" v="0.00" accent="primary" />
              </div>
            </TuiPanel>

            <TuiPanel label="/var/log/auth.log" innerClassName="p-4 md:p-5 grow">
              <div className="text-[11px] leading-[1.55] font-mono flex flex-col gap-0.5 tabular-nums">
                {liveLog.map((row, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="text-muted-foreground shrink-0">[{row.t}]</span>
                    <span
                      className={
                        row.c === "primary"
                          ? "text-primary"
                          : row.c === "destructive"
                            ? "text-destructive"
                            : row.c === "warning"
                              ? "text-[hsl(var(--warning))]"
                              : "text-foreground/70"
                      }
                    >
                      {row.m}
                    </span>
                  </div>
                ))}
              </div>
            </TuiPanel>
          </aside>
        </section>

        {/* ───── DIRECTORY LISTING: tracks ───── */}
        <section id="tracks" className="scroll-mt-24">
          <TuiPanel
            label="ls -la /opt/frisyre/tracks"
            right={`${tracks.length} entries`}
            innerClassName="p-0 overflow-x-auto"
          >
            <table className="min-w-[760px] w-full text-[12px] tabular-nums">
              <thead>
                <tr className="text-muted-foreground border-b border-border">
                  <th className="font-normal text-left uppercase text-[10px] tracking-[0.18em] px-4 py-2.5 w-28">perms</th>
                  <th className="font-normal text-left uppercase text-[10px] tracking-[0.18em] px-2 py-2.5 w-12">own</th>
                  <th className="font-normal text-right uppercase text-[10px] tracking-[0.18em] px-2 py-2.5 w-16">size</th>
                  <th className="font-normal text-left uppercase text-[10px] tracking-[0.18em] px-3 py-2.5 w-28">mtime</th>
                  <th className="font-normal text-left uppercase text-[10px] tracking-[0.18em] px-3 py-2.5">target</th>
                  <th className="font-normal text-right uppercase text-[10px] tracking-[0.18em] px-4 py-2.5 w-32">status</th>
                </tr>
              </thead>
              <tbody>
                {tracks.map((track, i) => {
                  const lessons = track.modules.reduce((s, m) => s + m.lessons.length, 0);
                  const sizeKb = (lessons * 4.2 + 2).toFixed(1);
                  const unlocked = progress
                    ? isTrackUnlocked(i, progress.completedLessons, tracks, progress.examScores, trackIds)
                    : i === 0;
                  const hasCert = !!progress?.certificates[track.id];
                  const isLocked = !unlocked;
                  return (
                    <tr
                      key={track.id}
                      className="border-b border-border/60 hover:bg-primary/5 transition-colors group"
                    >
                      <td className="px-4 py-3 text-muted-foreground">
                        {hasCert ? "-rwxr-xr-x" : isLocked ? "-rwxr-xr--" : "-rwsr-xr-x"}
                      </td>
                      <td className="px-2 py-3 text-foreground/70">root</td>
                      <td className="px-2 py-3 text-right text-muted-foreground">
                        {sizeKb}K
                      </td>
                      <td className="px-3 py-3 text-muted-foreground">
                        {`Mar ${String(2 + i).padStart(2, "0")} ${String(9 + i).padStart(2, "0")}:14`}
                      </td>
                      <td className="px-3 py-3">
                        <Link
                          to={`/track/${track.id}`}
                          className="flex items-baseline gap-3 group/link"
                        >
                          <span className="text-muted-foreground">{track.tier}/</span>
                          <span className="text-primary font-bold group-hover/link:underline underline-offset-4">
                            {track.id}/
                          </span>
                          <span className="hidden sm:inline text-foreground/70 truncate max-w-[28ch]">
                            {track.tagline}
                          </span>
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {hasCert ? (
                          <span className="text-[10px] uppercase tracking-[0.18em] text-primary">
                            ◆ cert
                          </span>
                        ) : isLocked ? (
                          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                            [locked]
                          </span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-[0.18em] text-primary">
                            ◆ active
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(() => {
                  const i = tracks.length;
                  const masterCert = progress?.certificates["__master__"];
                  const hasMaster = !!masterCert;
                  const allTrackCerts = progress
                    ? tracks.every((t) => !!progress.certificates[t.id])
                    : false;
                  const masterLocked = !allTrackCerts;
                  return (
                    <tr className="border-b border-border/60 hover:bg-primary/5 transition-colors group bg-primary/5">
                      <td className="px-4 py-3 text-muted-foreground">
                        {hasMaster ? "-rwxr-xr-x" : masterLocked ? "-rwxr-xr--" : "-rwsr-xr-x"}
                      </td>
                      <td className="px-2 py-3 text-primary font-bold">root</td>
                      <td className="px-2 py-3 text-right text-muted-foreground">9.6K</td>
                      <td className="px-3 py-3 text-muted-foreground">
                        {`Mar ${String(2 + i).padStart(2, "0")} ${String(9 + i).padStart(2, "0")}:14`}
                      </td>
                      <td className="px-3 py-3">
                        <Link
                          to={hasMaster ? "/certificate/master" : "/dashboard"}
                          className="flex items-baseline gap-3 group/link"
                        >
                          <span className="text-muted-foreground">20/</span>
                          <span className="text-primary font-bold group-hover/link:underline underline-offset-4">
                            master-certificate/
                          </span>
                          <span className="hidden sm:inline text-foreground/70 truncate max-w-[36ch]">
                            ★ FFCO — Full-Spectrum Cyber Operator (capstone)
                          </span>
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {hasMaster ? (
                          <span className="text-[10px] uppercase tracking-[0.18em] text-primary">
                            ★ master
                          </span>
                        ) : masterLocked ? (
                          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                            [locked]
                          </span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-[0.18em] text-primary">
                            ◆ ready
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })()}
              </tbody>
            </table>
          </TuiPanel>
        </section>

        {/* ───── MAN PAGE: how it works ───── */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          <TuiPanel
            label="man frisyre(1)"
            className="lg:col-span-8"
            innerClassName="p-5 md:p-6"
          >
            <h2 className="text-xs uppercase tracking-[0.2em] text-primary mb-3">NAME</h2>
            <p className="text-[13px] text-foreground/85 mb-5">
              <span className="text-foreground font-bold">frisyre</span> — a free,
              end-to-end cybersecurity training operating environment.
            </p>

            <h2 className="text-xs uppercase tracking-[0.2em] text-primary mb-3">SYNOPSIS</h2>
            <pre className="text-[12px] text-foreground/85 mb-5 leading-relaxed">{`frisyre <track> [--module=NAME] [--exam] [--lab=PROVIDER]
        [--certify] [--practice]`}</pre>

            <h2 className="text-xs uppercase tracking-[0.2em] text-primary mb-3">DESCRIPTION</h2>
            <ol className="text-[13px] text-foreground/85 space-y-2 list-none">
              {[
                "Pick a track. Each is a structured curriculum, sequenced from prerequisites up.",
                "Work through modules — every lesson links to the best free resource on the open web.",
                "Run drills inside the in-browser Kali playground; no install, no setup.",
                "Sit the timed exam (80% to pass). Pull a signed, printable certificate.",
              ].map((line, i) => (
                <li key={i} className="flex gap-3">
                  <span className="text-primary tabular-nums shrink-0">{String(i + 1).padStart(2, "0")}.</span>
                  <span>{line}</span>
                </li>
              ))}
            </ol>

            <h2 className="text-xs uppercase tracking-[0.2em] text-primary mt-5 mb-3">SEE ALSO</h2>
            <p className="text-[12px] text-muted-foreground">
              <Link to="/dashboard" className="text-foreground/80 hover:text-primary underline underline-offset-4">dashboard(1)</Link>,{" "}
              <Link to="/leaderboard" className="text-foreground/80 hover:text-primary underline underline-offset-4">leaderboard(1)</Link>,{" "}
              <Link to="/playground" className="text-foreground/80 hover:text-primary underline underline-offset-4">playground(1)</Link>
            </p>
          </TuiPanel>

          <TuiPanel
            label="cat ./principles"
            className="lg:col-span-4"
            innerClassName="p-5 md:p-6 flex flex-col gap-4"
          >
            {[
              { k: "FREE", v: "Skills that cost $1K–$7K elsewhere." },
              { k: "DEEP", v: "OSCP-level depth across 19 tracks." },
              { k: "OPEN", v: "Every resource is on the open web." },
              { k: "PROVEN", v: "Timed exams, signed certificates." },
            ].map((p) => (
              <div key={p.k} className="border-l-2 border-primary pl-3">
                <div className="text-[10px] uppercase tracking-[0.25em] text-primary font-bold">{p.k}</div>
                <div className="text-[12px] text-foreground/80 mt-1">{p.v}</div>
              </div>
            ))}
          </TuiPanel>
        </section>

        {/* ───── FOOTER LINE ───── */}
        <footer className="pt-6 mt-2 border-t border-border flex flex-col md:flex-row md:items-center justify-between gap-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          <span>EOF — frisyre_os // build 9.4.1 // since 2009 // {now.getUTCFullYear()}</span>
          <span className="flex flex-wrap gap-x-4 gap-y-1.5">
            <Link to="/auth" className="hover:text-primary">access</Link>
            <Link to="/leaderboard" className="hover:text-primary">leaderboard</Link>
            <Link to="/playground" className="hover:text-primary">playground</Link>
            <Link to="/privacy" className="hover:text-primary">privacy</Link>
            <Link to="/terms" className="hover:text-primary">terms</Link>
          </span>
        </footer>
      </main>
    </div>
  );
};

function Stat({ k, v, accent }: { k: string; v: string; accent?: "primary" | "warning" }) {
  return (
    <div className="flex justify-between border-b border-border/40 pb-2 last:border-0">
      <span className="text-muted-foreground uppercase tracking-[0.18em] text-[10px]">{k}</span>
      <span
        className={
          accent === "primary"
            ? "text-primary font-bold tabular-nums"
            : accent === "warning"
              ? "text-[hsl(var(--warning))] font-bold tabular-nums"
              : "text-foreground tabular-nums"
        }
      >
        {v}
      </span>
    </div>
  );
}

export default Index;
